import React, { SelectHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  tag?: string;
}

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'size'> {
  label?: string;
  error?: string;
  placeholder?: string;
  options: SelectOption[] | Record<string, SelectOption[]>;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  required?: boolean;
  disabledGroup?: string;
  size?: 'sm' | 'md' | 'xs';
}

const Select: React.FC<SelectProps> = ({
  className,
  label,
  placeholder,
  value,
  onChange,
  error,
  required,
  disabled,
  disabledGroup,
  options,
  size = 'md',
  ...props
}) => {
  return (
    <div className={cn(size === 'sm' ? 'space-y-1' : 'space-y-2')}>
      {label && (
        <label
          className={`block font-medium text-foreground ${size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-sm' : 'text-md'}`}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <div className="relative w-full">
        <select
          className={cn(
            'block w-full pr-8 border border-border shadow-sm bg-card text-foreground appearance-none focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors',
            size === 'sm'
              ? 'px-2 py-1 rounded-md text-sm'
              : 'px-3 py-2 rounded-lg',
            error &&
              'border-destructive focus:ring-destructive focus:border-destructive',
            disabled && 'bg-muted cursor-not-allowed',
            className
          )}
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...props}
        >
          {placeholder && (
            <option value="" disabled className="text-muted-foreground">
              {placeholder}
            </option>
          )}
          {Array.isArray(options)
            ? options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}{' '}
                  {option?.tag && (
                    <span className="text-muted-foreground text-sm">
                      {option.tag}
                    </span>
                  )}
                </option>
              ))
            : Object.entries(options).map(([key, value]) => (
                <optgroup
                  key={key}
                  label={key}
                  disabled={disabledGroup === key}
                >
                  {value.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}{' '}
                      {option?.tag && (
                        <span className="text-muted-foreground text-sm">
                          {option.tag}
                        </span>
                      )}
                    </option>
                  ))}
                </optgroup>
              ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          <ChevronDown className="w-4 h-4" />
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default Select;
